<script setup>
import { useAuth } from '/app/composables/auth';
import { useChannel } from '/app/composables/pusher';
import { useRoute } from 'vue-router';
import { bindEmit } from '/app/plugins/bus';
import { useBrain } from '/app/queries/gamedash';
import { useQueryClient } from '@tanstack/vue-query';
import { syncRef } from '@vueuse/core'
import { ref, computed, provide } from 'vue';
import { ACTIVITY_RECEIVED } from '/app/constant/game-dash';

const route = useRoute();
const { auth } = useAuth();
const handle = computed(() => auth.user.screen_name);

const roles = {
    scout: 'scout',
    rater: 'analyst',
    mentor: 'mentor',
    patron: 'patron',
    director: 'director',
    catalyst: 'catalyst',
    insightful: 'insightful',
    innovator: 'overall',
    olympiads: 'director',
};
const brain = ref(null);
const role = computed(() => {
    let path = route.path.split('/').filter(segment => segment);

    return path[2] ? (roles[path[2]] || 'overall') : 'overall';
});
const params = computed(() => ({
    role: role.value,
}))

const { data } = useBrain(handle, params);

syncRef(data, brain, { direction: 'ltr' })

provide('gamedash', {
    brain, role,
});

const updateBrainScore = (data) => {
    if (['overall', data.role].includes(role.value)) {
        brain.value = {
            ...brain.value,
            ...data,
        };
    }
}

const queryClient = useQueryClient();
const emitNeuronEarned = bindEmit('brain:neuron-earned');
const emitHighlight = bindEmit('game-dash:highlight');

// Every push is unread activity until the dash is opened, so each listener
// announces it alongside its own event. Emitted here rather than inside
// `useChannel` so only gamedash channels feed the bell badge.
const emitActivity = bindEmit(ACTIVITY_RECEIVED);

// Real-time neurons arrive without the admin-template catalog, so prepend the
// raw payload onto the cached first page — useNeurons hydrates at read time
// with the templates that came in the first request.
const prependNeuron = (data) => {
    queryClient.setQueriesData({ queryKey: ['gamedash', 'neurons'] }, (cache) => {
        if (!cache?.pages?.length) return cache;

        const [first, ...rest] = cache.pages;

        return {
            ...cache,
            pages: [
                { ...first, data: [data, ...(first.data || [])] },
                ...rest,
            ],
        };
    });
};

// Derived from auth rather than gated behind a bus event, so the channel
// follows login/logout and the listeners bind in this component's scope.
// `useChannel` holds the events until the subscription is authorised.
const channelName = computed(() => auth.user?.id ? `private-gamedash-${auth.user.id}` : null);
const { listen } = useChannel(channelName);

listen('gamedash-highlights', (data) => {
    emitHighlight(data);
    emitActivity();
});
listen('brain__neuron-earned', (data) => {
    prependNeuron(data);
    emitNeuronEarned(data);
    emitActivity();
});
listen('brain__score-updated', (data) => {
    updateBrainScore(data);
    emitActivity();
});

</script>

<template>
    <slot />
</template>